import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import type { ScreenPosition } from './dragDropTypes';

import { store } from '@/editor/store';

/**
 * Finds the best placement position by screen position
 * @param screenPosition - The screen position
 * @param camera - The camera
 * @param canvas - The canvas
 * @param fallbackDistance - The distance to the camera, if camera doesn't hit 0 height (parallel to horizontal plane or below it)
 * @returns The best placement position
 */
export const findBestPlacementPosition = (screenPosition: ScreenPosition, camera: THREE.Camera, canvas: HTMLCanvasElement, fallbackDistance: number = 10, sourceActor?: ENGINE.Actor) => {
  const bounds = canvas.getBoundingClientRect();
  const x = ((screenPosition.x - bounds.left) / bounds.width) * 2 - 1;
  const y = -((screenPosition.y - bounds.top) / bounds.height) * 2 + 1;

  const position = findBestPlacementPositionByNormalizedScreenPosition({ x, y }, camera, fallbackDistance, sourceActor);

  // Apply snap values if enabled
  const snap = store.snapValues;
  if (snap.translateEnabled) {
    position.x = Math.round(position.x / snap.translate) * snap.translate;
    position.y = Math.round(position.y / snap.translate) * snap.translate;
    position.z = Math.round(position.z / snap.translate) * snap.translate;
  }

  return position;
};


/**
 * Finds the best placement position by normalized screen position
 * @param normalizedScreenPosition - The normalized screen position, in the range of -1 to 1
 * @param camera - The camera
 * @param fallbackDistance - The distance to the camera, if camera doesn't hit 0 height (parallel to horizontal plane or below it)
 * @returns The best placement position
 */
export const findBestPlacementPositionByNormalizedScreenPosition = (
  normalizedScreenPosition: ScreenPosition,
  camera: THREE.Camera,
  fallbackDistance: number = 10,
  sourceActor?: ENGINE.Actor,
): THREE.Vector3 => {
  const { x, y } = normalizedScreenPosition;
  const origin = camera.position.clone();
  const dir = new THREE.Vector3(x, y, 0.5).unproject(camera).sub(origin).normalize();
  const raycaster = new THREE.Raycaster(origin, dir);

  const scene = store.scene.scene;
  if (!scene) return getFallbackGroundPosition(origin, dir, fallbackDistance, sourceActor);

  // Build ignore set for faster lookups
  const ignoreObjects = new Set<THREE.Object3D>();
  if (sourceActor) {
    ignoreObjects.add(sourceActor.rootComponent);
  }
  store.scene.selectedActors.forEach(actor => ignoreObjects.add(actor.rootComponent));

  const shouldIgnoreObject = (obj: THREE.Object3D): boolean => {
    if (ignoreObjects.has(obj)) return true;
    if (obj instanceof THREE.Sprite) return true;
    if (obj.userData?.isViewportGrid ?? false) return true;
    // if (obj.userData?.isSelectionHelper ?? false) return true;
    // if (obj.userData?.isTransformControls ?? false) return true;
    // if (obj.userData?.isTransformControlsGizmo ?? false) return true;
    // if (obj.userData?.isTransformControlsPlane ?? false) return true;
    if (obj === store.scene.selectionGroup) return true;
    if (obj === store.scene.hiddenGroup) return true;
    return false;
  };

  // Collect raycast targets efficiently
  const raycastTargets: THREE.Object3D[] = [];
  const collectMeshObjects = (obj: THREE.Object3D) => {
    if (obj instanceof THREE.Mesh && obj.visible && obj.matrixWorld) {
      raycastTargets.push(obj);
    }
    for (const child of obj.children) {
      if (!(child instanceof THREE.Sprite) && !child.userData?.isSelectionHelper) {
        collectMeshObjects(child);
      }
    }
  };

  for (const child of scene.children) {
    if (!shouldIgnoreObject(child)) collectMeshObjects(child);
  }

  // Perform raycast and filter results
  const intersects = raycaster.intersectObjects(raycastTargets, false);
  const validIntersect = intersects.find(intersect => {
    if (!intersect.object) return false;
    let current: THREE.Object3D | null = intersect.object;
    while (current) {
      if (shouldIgnoreObject(current)) {
        return false;
      }
      current = current.parent;
    }
    return true;
  });

  if (validIntersect) {
    const intersectionPoint = validIntersect.point;

    // The normal from raycast intersection is already in world space
    if (!validIntersect.normal) {
      console.warn('Raycast intersection missing normal, using fallback');
    }

    let normal = validIntersect.normal ?? new THREE.Vector3(0, 1, 0); // Default to up vector

    // Debug: Log the original normal and face
    // console.log('Original normal:', normal.clone());
    // console.log('Ray direction:', dir.clone());
    // console.log('Face index:', validIntersect.faceIndex);
    // console.log('Object:', validIntersect.object);

    // Handle incorrect normals from geometry
    // For ground planes, we expect the normal to point upward (positive Y)
    // If we're hitting a horizontal surface but the normal is pointing in wrong direction,
    // we need to correct it based on the geometry and context

    // console.log('Final normal:', normal);
    // console.log('intersectionPoint', intersectionPoint);
    const offset = calculateOffset(sourceActor, intersectionPoint, normal);
    return intersectionPoint.add(offset);
    // return intersectionPoint;
  }

  return getFallbackGroundPosition(origin, dir, fallbackDistance, sourceActor);
};

/** Calculate offset based on surface normal and actor bounds to avoid overlap */
const calculateOffset = (sourceActor?: ENGINE.Actor, intersectionPoint?: THREE.Vector3, surfaceNormal?: THREE.Vector3): THREE.Vector3 => {
  let offset = new THREE.Vector3();

  if (sourceActor) {
    const boundingBox = getActorBounds(sourceActor);

    if (boundingBox && !boundingBox.isEmpty()) {
      // Use surface normal if available, otherwise default to up vector
      const normal = surfaceNormal?.clone().normalize() ?? new THREE.Vector3(0, 1, 0);

      // Get the actor's current world position (pivot point)
      const actorPosition = sourceActor.getWorldPosition();

      // Calculate how far the bottom of the bounding box is from the pivot point
      // This accounts for models with off-center pivots
      const bottomPoint = new THREE.Vector3(
        actorPosition.x,
        boundingBox.min.y, // Use the actual bottom Y coordinate
        actorPosition.z
      );

      // Calculate the vector from pivot to bottom point
      const pivotToBottom = bottomPoint.clone().sub(actorPosition);

      // Project this vector onto the surface normal to get the offset distance
      const offsetDistance = Math.abs(pivotToBottom.dot(normal));

      // Offset the actor by this distance along the normal
      offset = normal.multiplyScalar(offsetDistance);
    } else {
      // console.warn('Bounding box is empty or null, using default offset');
      offset.y = 0.5;
    }
  } else {
    // Default offset to prevent objects from sinking into surfaces
    offset.y = 0.5;
  }

  return offset;
};

/** Get fallback ground position when raycast fails */
const getFallbackGroundPosition = (origin: THREE.Vector3, dir: THREE.Vector3, fallbackDistance: number, sourceActor?: ENGINE.Actor): THREE.Vector3 => {
  let t = fallbackDistance;
  if (dir.y !== 0) {
    const test_t = -origin.y / dir.y;
    if (test_t > 0) t = test_t;
  }

  const groundPosition = origin.clone().add(dir.multiplyScalar(t));

  if (sourceActor) {
    const boundingBox = getActorBounds(sourceActor);
    if (boundingBox && !boundingBox.isEmpty()) {
      const boundingBoxSize = new THREE.Vector3();
      boundingBox.getSize(boundingBoxSize);
      const halfExtents = boundingBoxSize.multiplyScalar(0.5);
      groundPosition.y += halfExtents.y;
    }
  }
  return groundPosition;
};

/**
 * Get the bounding box of an object or array of objects
 * @param target - The object or array of objects to get the bounding box of
 * @returns The bounding box of the object(s)
 */
export function getBounds(target: THREE.Object3D | THREE.Object3D[]): THREE.Box3 {
  const objects = Array.isArray(target) ? target : [target];
  const boundingBox = new THREE.Box3();
  objects.forEach(object => {
    if (object) {
      const objectBounds = new THREE.Box3().setFromObject(object);
      if (!objectBounds.isEmpty()) boundingBox.union(objectBounds);
    }
  });
  return boundingBox;
}

/**
 * Get the bounding box of an actor or array of actors
 * @param target - The actor or array of actors to get the bounding box of
 * @returns The bounding box of the actor(s)
 */
export function getActorBounds(target: ENGINE.Actor | ENGINE.Actor[]): THREE.Box3 {
  const actors = Array.isArray(target) ? target : [target];
  const boundingBox = new THREE.Box3();
  for (const actor of actors) {
    if (actor instanceof ENGINE.Actor) {
      const actorBounds = actor.calcBoundingBox();
      if (!actorBounds.isEmpty()) {
        boundingBox.union(actorBounds);
      }
    }
  }
  return boundingBox;
}

/** Calculate differences between two arrays */
export function calculateArrayDiff<T>(oldArray: T[], newArray: T[]) {
  const added = newArray.filter(item => !oldArray.includes(item));
  const removed = oldArray.filter(item => !newArray.includes(item));
  return { added, removed };
}